import { create } from "zustand";
import { jwtDecode } from "jwt-decode";

// Keep it self-contained so you don't need extra files
export interface User {
  userId: number;
  username: string;
  emailId: string;
  oemId: number;
  role: string;
  vendorId: number;
}

type JwtPayload = {
  nameid?: string;
  sub?: string;
  email?: string;
  role?: string;
  vendorId?: string;
  oemId?: string;
  exp?: number;
};

type State = {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;

  setTokens: (access: string, refresh: string) => void;
  setUserFromAccess: (access: string) => void;
  peekDecoded: () => JwtPayload | null;
  logout: () => void;
};

const STORAGE_KEY = "auth-state";

function resolveOemIdFromBaseName(): number {
  const baseName = String(process.env.REACT_APP_BASE_NAME || "").trim();
  const match = baseName.match(/oem(\d+)/i);
  return match ? Number(match[1]) : 0;
}

const load = (): Partial<State> | null => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};

export const useUserStore = create<State>((set, get) => ({
  user: load()?.user ?? null,
  accessToken: load()?.accessToken ?? null,
  refreshToken: load()?.refreshToken ?? null,

  setTokens: (access, refresh) => {
    // Save tokens
    set({ accessToken: access, refreshToken: refresh });
    // Decode and populate user
    try {
      const p = jwtDecode<JwtPayload>(access);
      const oemIdFromBase = resolveOemIdFromBaseName();
      const user: User = {
        userId: Number(p.nameid ?? p.sub ?? 0),
        username: (p.email ?? "").split("@")[0],
        emailId: p.email ?? "",
        // Always derive OEM from app basename (example: /OEM4 -> 4)
        oemId: oemIdFromBase,
        role: String(p.role ?? ""),
        vendorId: Number(p.vendorId ?? 0),
      };
      set({ user });
    } catch {
      // ignore decode errors
    }
    persist();
  },

  setUserFromAccess: (access) => {
    try {
      const p = jwtDecode<JwtPayload>(access);
      const oemIdFromBase = resolveOemIdFromBaseName();
      const user: User = {
        userId: Number(p.nameid ?? p.sub ?? 0),
        username: (p.email ?? "").split("@")[0],
        emailId: p.email ?? "",
        // Always derive OEM from app basename (example: /OEM4 -> 4)
        oemId: oemIdFromBase,
        role: String(p.role ?? ""),
        vendorId: Number(p.vendorId ?? 0),
      };
      set({ user, accessToken: access });
      persist();
    } catch {
      // ignore
    }
  },

  peekDecoded: () => {
    const t = get().accessToken;
    if (!t) return null;
    try {
      return jwtDecode<JwtPayload>(t);
    } catch {
      return null;
    }
  },

  logout: () => {
    set({ user: null, accessToken: null, refreshToken: null });
    localStorage.removeItem(STORAGE_KEY);
  },
}));

function persist() {
  const { user, accessToken, refreshToken } = useUserStore.getState();
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ user, accessToken, refreshToken })
  );
}
